import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import type { Asset, Query, QuerySecureReportDownloadArgs } from '@/types/GraphqlSchema';

const openDownloadUrl = (url: string, filename: string) => {
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';

  anchor.click();
};

export type SecureReportDownloadQueryData = Pick<Query, 'secureReportDownload'>;

export const SECURE_REPORT_DOWNLOAD_QUERY: TypedDocumentNode<
  SecureReportDownloadQueryData,
  QuerySecureReportDownloadArgs
> = gql`
  query secureReportDownload($assetLegacyId: ID!, $isoCode: String!, $reportType: String!) {
    secureReportDownload(
      assetLegacyId: $assetLegacyId
      isoCode: $isoCode
      reportType: $reportType
    ) {
      url
      filename
      downloadMessage
    }
  }
`;

const DOWNLOAD_ERROR_MESSAGE = 'Unable to start the download. Please try again.';

export const useReportDownloads = (asset: Asset | null) => {
  const { setSnack } = useSnackBarContext();
  const previousDataRef = useRef<SecureReportDownloadQueryData | null>(null);

  const assetLegacyId = asset?.legacyId;
  const isoCode = asset?.country?.isoCode;

  const [getReportDownload, { loading, data, error }] = useLazyQuery(SECURE_REPORT_DOWNLOAD_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!data || data === previousDataRef.current) return;

    previousDataRef.current = data;

    const secureDownload = data.secureReportDownload;
    const url = secureDownload?.url;
    const filename = secureDownload?.filename;

    if (!url || !filename) {
      setSnack({
        open: true,
        message: DOWNLOAD_ERROR_MESSAGE,
        severity: 'error',
      });
      return;
    }

    const downloadMessage = secureDownload.downloadMessage ?? '';
    setSnack({
      open: true,
      message: `Your download should begin shortly.${downloadMessage}`,
      severity: 'success',
    });
    openDownloadUrl(url, filename);
  }, [data, setSnack]);

  useEffect(() => {
    if (!error) return;

    setSnack({
      severity: 'error',
      message: error.message,
      open: true,
    });
  }, [error, setSnack]);

  const downloadReport = useCallback(
    (reportType: string): void => {
      if (!assetLegacyId || !isoCode) return;

      getReportDownload({
        variables: { assetLegacyId, isoCode, reportType },
      });
    },
    [assetLegacyId, isoCode, getReportDownload],
  );

  return {
    downloadReport,
    loading,
  };
};
